import React, { useState, useEffect } from 'react';
import transactionData from '../../../Pages/AppPAges/DashUi/Jsonfilegenerate/transactions.json';
import { RotateCcw } from 'lucide-react';

function Transaction() {
  const [originalRandomFive, setOriginalRandomFive] = useState([]);
  const [liveTransactions, setLiveTransactions] = useState([]);
  const [displayedTransactions, setDisplayedTransactions] = useState([]);
  const [filters, setFilters] = useState({
    dateRange: '',
    transactionType: '',
    fuelType: '',
    searchTerm: ''
  });
  const [isLiveMode, setIsLiveMode] = useState(false);
  const [nextLiveIndex, setNextLiveIndex] = useState(5);
  const [selectedStore, setSelectedStore] = useState('Marion-Sunoco-Gas-Station-0290');

  const storeList = [
    'Marion-Sunoco-Gas-Station-0290',
    'Main-Street-Gas-Station-1101',
    'Lakeside-Fuel-Center-8892',
    'Highway-Petrol-Depot-4421',
    'CityFuel-Station-7720'
  ];

  useEffect(() => {
    const fullData = transactionData.transactions;
    const shuffled = [...fullData].sort(() => 0.5 - Math.random());
    const fiveRandom = shuffled.slice(0, 5);
    setOriginalRandomFive(fiveRandom);
    setDisplayedTransactions(fiveRandom);
    setLiveTransactions([]);
    setNextLiveIndex(5);
  }, []);

  const getFilterBase = () => {
    return [...liveTransactions, ...originalRandomFive];
  };

  useEffect(() => {
    const noFiltersApplied = Object.values(filters).every(v => !v);

    if (noFiltersApplied) {
      setDisplayedTransactions(getFilterBase());
      return;
    }

    let filtered = getFilterBase();

    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(
        t =>
          (t.type && t.type.toLowerCase().includes(term)) ||
          (t.transactionId && t.transactionId.toLowerCase().includes(term))
      );
    }

    if (filters.transactionType) {
      filtered = filtered.filter(t => t.category === filters.transactionType);
    }

    if (filters.fuelType) {
      filtered = filtered.filter(t => t.fuelType === filters.fuelType);
    }

    if (filters.dateRange) {
      const filterDate = new Date(filters.dateRange);
      filtered = filtered.filter(t => {
        const transactionDate = new Date(t.date);
        return transactionDate.toDateString() === filterDate.toDateString();
      });
    }

    setDisplayedTransactions(filtered);
  }, [filters, originalRandomFive, liveTransactions]);

  const handleLiveTransaction = () => {
    if (nextLiveIndex >= transactionData.transactions.length) {
      alert('No more new transactions available.');
      return;
    }

    setIsLiveMode(true);

    setTimeout(() => {
      const newLive = {
        ...transactionData.transactions[nextLiveIndex],
        status: 'live'
      };

      setLiveTransactions(prev => [newLive, ...prev]);
      setNextLiveIndex(prev => prev + 1);
      setIsLiveMode(false);
    }, 3000);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilter = filterType => {
    setFilters(prev => ({
      ...prev,
      [filterType]: ''
    }));
  };

  const getTransactionIcon = type => {
    if (type.includes('Fuel Sale')) {
      return '⛽';
    }
    return '🛒';
  };

  const getAppliedFilters = () => {
    const applied = [];
    if (filters.transactionType) applied.push({ type: 'transactionType', value: filters.transactionType });
    if (filters.fuelType) applied.push({ type: 'fuelType', value: filters.fuelType });
    if (filters.searchTerm) applied.push({ type: 'searchTerm', value: filters.searchTerm });
    if (filters.dateRange) applied.push({ type: 'dateRange', value: filters.dateRange });
    return applied;
  };

  const formatAmountColor = amount => {
    if (amount.includes('$0.00')) return '#999';
    return '#2563eb';
  };

  return (
    <div className='responsive-box'>
      <div style={styles.liveTransactionTitle}>
        <RotateCcw size={24} color="#333" />
        Live Transaction
      </div>
      <div style={styles.transactionHeader}>
        <div style={styles.storeSelector}>
          <h2 style={styles.transactionTitle}>
            <span style={styles.liveIndicator}></span>
            {selectedStore}
          </h2>
          <select
            style={styles.storeDropdown}
            value={selectedStore}
            onChange={(e) => setSelectedStore(e.target.value)}
          >
            {storeList.map((store, index) => (
              <option key={index} value={store}>
                {store}
              </option>
            ))}
          </select>

        </div>

        <button
          style={{
            ...styles.liveTransactionBtn,
            backgroundColor: isLiveMode ? '#94a3b8' : '#3b82f6'
          }}
          onClick={handleLiveTransaction}
          disabled={isLiveMode}
        >
          {isLiveMode ? 'Loading...' : 'Live Transaction'}
        </button>
      </div>


      <div style={styles.transactionLayout}>

        <div style={styles.transactionList}>
          {isLiveMode && (
            <div style={{ ...styles.transactionItem, ...styles.liveTransaction }}>
              <div style={styles.transactionIcon}>
                <span style={styles.iconText}>⏳</span>
              </div>
              <div style={styles.transactionInfo}>
                <div style={styles.transactionType}>Loading Live Transaction...</div>
                <div style={styles.transactionId}>Please wait...</div>
                <div style={styles.transactionDate}>Processing...</div>
              </div>
            </div>
          )}

          {displayedTransactions.length === 0 ? (
            <div style={styles.noTransactions}>
              No transactions found matching your filters.
            </div>
          ) : (
            displayedTransactions.map(transaction => (
              <div
                key={transaction.id}
                style={{
                  ...styles.transactionItem,
                  ...(transaction.status === 'live' ? styles.liveTransaction : {})
                }}
              >
                <div
                  style={{
                    ...styles.transactionIcon,
                    backgroundColor: transaction.category === 'Fuel Sale' ? '#dbeafe' : '#fef3c7'
                  }}
                >
                  <span style={styles.iconText}>{getTransactionIcon(transaction.type)}</span>
                </div>
                <div style={styles.transactionInfo}>
                  <div style={styles.transactionType}>
                    {transaction.type}
                    {transaction.status === 'live' && (
                      <span style={styles.liveBadge}>New</span>
                    )}
                  </div>
                  <div style={styles.transactionId}>{transaction.transactionId}</div>
                  <div style={styles.transactionDate}>{transaction.date}</div>
                </div>
                <div style={styles.transactionRightSection}>
                  <div style={styles.transactionAmount}>
                    {transaction.amount}
                  </div>
                  <div style={styles.transactionTax}>
                    Total Tax: {transaction.tax}
                  </div>
                  <div style={styles.transactionDuration}>
                    {transaction.duration}
                  </div>
                </div>

              </div>
            ))
          )}
        </div>

        <div style={styles.filtersPanel}>
          <h3 style={styles.filtersTitle}>Filters</h3>

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Select Date Range</label>
            <input
              type="date"
              style={styles.filterInput}
              value={filters.dateRange}
              onChange={e => handleFilterChange('dateRange', e.target.value)}
            />
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Transaction Type</label>
            <select
              style={styles.filterSelect}
              value={filters.transactionType}
              onChange={e => handleFilterChange('transactionType', e.target.value)}
            >
              <option value="">All Types</option>
              <option value="Sale Transaction">Sale Transaction</option>
              <option value="Fuel Sale">Fuel Sale</option>
            </select>
          </div>

          { }

          {getAppliedFilters().length > 0 && (
            <div style={styles.appliedFilters}>
              <h4 style={styles.appliedFiltersTitle}>Applied Filters</h4>
              {getAppliedFilters().map((filter, index) => (
                <span key={index} style={styles.appliedFilterTag}>
                  {filter.value}
                  <button
                    style={styles.removeFilter}
                    onClick={() => clearFilter(filter.type)}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  dashboardContainer: {
    backgroundColor: '#f8fafc',
    minHeight: 'auto',
    padding: '20px',
    marginTop: '20px',
    width: "100%",
    borderRadius: "20px",
    maxWidth: "1500px",
    margin: "20px auto",
    fontFamily: 'Arial, sans-serif'
  },
  transactionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  transactionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#374151',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    margin: 0
  },
  liveIndicator: {
    width: '8px',
    height: '8px',
    backgroundColor: '#22c55e',
    borderRadius: '50%',
    display: 'inline-block'
  },
  liveTransactionBtn: {
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  },
  transactionLayout: {
    display: 'flex',
    gap: '20px'
  },
  transactionList: {
    flex: 1,
    backgroundColor: 'white',
    borderRadius: '20px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden'
  },
  transactionItem: {
    padding: '16px',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },

  transactionIcon: {
    width: '32px',
    height: '32px',
    backgroundColor: '#f3f4f6',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  iconText: {
    fontSize: '16px'
  },
  transactionInfo: {
    flex: 1,
    minWidth: 0
  },
  transactionType: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  transactionId: {
    fontSize: '12px',
    color: '#6b7280',
    marginBottom: '2px'
  },
  transactionDate: {
    fontSize: '12px',
    color: '#6b7280'
  },
  // transactionAmounts: {
  //   display: 'flex',
  //   flexDirection: 'column',
  //   alignItems: 'flex-end',
  //   gap: '4px',
  //   marginRight: '20px'
  // },
  // transactionAmount: {
  //   fontSize: '16px',
  //   fontWeight: '600',
  //   color: '#2563eb'
  // },
  // transactionTax: {
  //   fontSize: '12px',
  //   color: '#6b7280'
  // },
  // transactionDuration: {
  //   fontSize: '12px',
  //   color: '#6b7280',
  //   textAlign: 'right',
  //   minWidth: '60px'
  // },
  liveBadge: {
    backgroundColor: '#22c55e',
    color: 'white',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: '500'
  },
  filtersPanel: {
    width: '280px',
    backgroundColor: 'white',
    borderRadius: '20px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    padding: '20px',
    height: 'fit-content'
  },
  filtersTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '20px',
    margin: '0 0 20px 0'
  },
  filterGroup: {
    marginBottom: '20px'
  },
  filterLabel: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '8px'
  },
  filterInput: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: 'white',
    boxSizing: 'border-box'
  },
  filterSelect: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: 'white',
    boxSizing: 'border-box'
  },
  filterButtons: {
    display: 'flex',
    gap: '8px'
  },
  filterBtn: {
    padding: '8px 16px',
    border: '1px solid #d1d5db',
    backgroundColor: 'white',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  filterBtnActive: {
    backgroundColor: '#3b82f6',
    color: 'white',
    borderColor: '#3b82f6'
  },
  appliedFilters: {
    marginTop: '20px'
  },
  appliedFiltersTitle: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '12px',
    margin: '0 0 12px 0'
  },
  appliedFilterTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: '#e5e7eb',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    color: '#374151',
    marginRight: '8px',
    marginBottom: '8px'
  },
  removeFilter: {
    background: 'none',
    border: 'none',
    color: '#6b7280',
    cursor: 'pointer',
    fontSize: '16px',
    lineHeight: '1'
  },
  noTransactions: {
    textAlign: 'center',
    padding: '40px',
    color: '#6b7280',
    fontSize: '14px'
  },
  storeSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  storeDropdown: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    backgroundColor: 'white',
    cursor: 'pointer'
  },
  liveTransactionTitle: {
    display: 'flex',
    alignItems: 'center',
    // marginLeft:"20px",
    marginBottom: "20px",
    gap: '12px',
    fontSize: '24px',
    fontWeight: 500,
    color: '#333',
    // backgroundColor: '#f9f9f9',
    padding: '8px 12px',
    borderRadius: '4px',
    // boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    width: 'fit-content',

  }, transactionRightSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    minWidth: '120px' // fixed width to help with alignment
  },
  transactionAmount: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#2563eb',
    whiteSpace: 'nowrap'
  },
  transactionTax: {
    fontSize: '12px',
    color: '#6b7280',
    whiteSpace: 'nowrap'
  },
  transactionDuration: {
    fontSize: '12px',
    color: '#6b7280',
    whiteSpace: 'nowrap'
  }


};

export default Transaction;